import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, ArrowRight, CheckCircle, Clock, User, Calendar, TrendingUp, Lightbulb, Target, AlertCircle } from 'lucide-react';
import { Breadcrumb } from '../../components/Breadcrumb';
import { Schema } from '../../components/Schema';
import { useBanner } from '../../contexts/BannerContext';

export const AIImplementationGuidePage: React.FC = () => {
  const { isBannerVisible, bannerHeight } = useBanner();

  const implementationSteps = [
    {
      step: 1,
      title: "Assess Your Current Business Processes",
      description: "Identify repetitive tasks, data-heavy processes, and areas where human decision-making could be enhanced with AI insights.",
      details: [
        "Map out daily workflows and identify bottlenecks",
        "Collect data on time spent on manual tasks",
        "Survey employees about their biggest productivity challenges",
        "Document current technology stack and data sources"
      ]
    },
    {
      step: 2,
      title: "Define Clear AI Objectives and Success Metrics",
      description: "Set specific, measurable goals for your AI implementation to ensure ROI and track progress effectively.",
      details: [
        "Define specific KPIs (cost reduction, time savings, accuracy improvement)",
        "Set realistic timelines and budgets",
        "Identify which departments will benefit most from AI",
        "Establish baseline metrics to measure improvement"
      ]
    },
    {
      step: 3,
      title: "Choose the Right AI Solutions",
      description: "Select AI technologies that align with your business needs, budget, and technical capabilities.",
      details: [
        "Start with proven solutions (chatbots, automation tools)",
        "Consider cloud-based vs. on-premise solutions",
        "Evaluate integration capabilities with existing systems",
        "Compare vendors and implementation partners"
      ]
    },
    {
      step: 4,
      title: "Partner with Experienced AI Providers",
      description: "Work with established AI companies that understand your industry and can provide ongoing support.",
      details: [
        "Look for providers with industry-specific experience",
        "Verify technical expertise and support capabilities",
        "Check references and case studies",
        "Ensure they offer training and change management support"
      ]
    },
    {
      step: 5,
      title: "Start with Pilot Projects",
      description: "Begin with small, low-risk implementations to prove ROI before scaling across your organization.",
      details: [
        "Select high-impact, low-complexity use cases",
        "Set short timelines (30-90 days) for initial results",
        "Gather feedback from end users throughout the pilot",
        "Document lessons learned and best practices"
      ]
    },
    {
      step: 6,
      title: "Scale Successful Implementations",
      description: "Expand proven AI solutions across departments and integrate more advanced capabilities.",
      details: [
        "Roll out successful pilots to similar use cases",
        "Integrate AI solutions across departments",
        "Add more sophisticated AI capabilities gradually",
        "Continuously monitor and optimize performance"
      ]
    },
    {
      step: 7,
      title: "Provide Team Training and Support",
      description: "Ensure your team is comfortable with new AI tools through comprehensive training and ongoing support.",
      details: [
        "Conduct hands-on training sessions for all users",
        "Create documentation and video tutorials",
        "Establish internal AI champions and support networks",
        "Provide ongoing education on AI developments"
      ]
    }
  ];

  const commonMistakes = [
    {
      mistake: "Trying to implement too much at once",
      solution: "Start small with pilot projects and scale gradually"
    },
    {
      mistake: "Not involving end users in the planning process",
      solution: "Include employees who will use AI tools in decision-making"
    },
    {
      mistake: "Focusing only on technology without considering process changes",
      solution: "Plan for workflow changes and provide change management support"
    },
    {
      mistake: "Underestimating the importance of data quality",
      solution: "Clean and organize data before implementing AI solutions"
    },
    {
      mistake: "Not setting clear success metrics",
      solution: "Define specific, measurable KPIs before implementation begins"
    }
  ];

  const aiSolutions = [
    {
      title: "Customer Service Chatbots",
      timeframe: "2-4 weeks typical*",
      roi: "Potential reduction in support workload*",
      difficulty: "Low"
    },
    {
      title: "Marketing Automation",
      timeframe: "4-6 weeks typical*",
      roi: "May improve lead management efficiency*",
      difficulty: "Medium"
    },
    {
      title: "Predictive Analytics",
      timeframe: "2-4 months typical*",
      roi: "Designed to improve forecasting capabilities*",
      difficulty: "High"
    },
    {
      title: "Process Automation (RPA)",
      timeframe: "6-12 weeks typical*",
      roi: "Potential reduction in manual processing*",
      difficulty: "Medium"
    }
  ];

  return (
    <>
      <Schema 
        type="article" 
        data={{
          headline: "How to Implement AI in Your Business: A Step-by-Step Guide for 2025",
          description: "Complete guide to implementing AI in your business, including step-by-step instructions, common mistakes to avoid, and proven strategies for success.",
          datePublished: "2025-01-18T10:00:00Z",
          dateModified: "2025-01-18T10:00:00Z"
        }} 
      />
      <Breadcrumb />
      <main className="pb-20">
        {/* Article Header */}
        <article 
          className="px-4 sm:px-6 lg:px-8"
          style={{ 
            paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px'
          }}
        >
          <div className="max-w-4xl mx-auto">
            {/* Article Meta */}
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-6">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                  January 18, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                  15 min read
                </div>
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                  Cognitive Craft Hub Team
                </div>
                <div className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-xs font-medium">
                  <Brain className="w-3 h-3 mr-1" />
                  AI Implementation
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent leading-tight">
                How to Implement AI in Your Business: A Step-by-Step Guide for 2025
              </h1>
              
              <p className="text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
                Discover the proven framework for successfully implementing AI in your business, from initial assessment to full-scale deployment. This comprehensive guide has helped hundreds of companies across Michigan, Ohio, Texas, Illinois, and Indiana transform their operations with AI.
              </p>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg dark:prose-invert max-w-none">
              
              {/* Introduction */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Why Every Business Needs an AI Implementation Strategy</h2>
                
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  Artificial Intelligence is no longer a futuristic concept—it's a present-day business opportunity. Companies that successfully implement AI may see improvements in efficiency, cost optimization, and competitive positioning in their markets. Results vary significantly based on implementation approach, business environment, and execution quality.
                </p>
                
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  However, many businesses struggle with where to start. This guide provides a proven, step-by-step framework that removes the guesswork and provides clear, actionable steps for AI implementation success.
                </p>

                <div className="bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/20 dark:to-transparent p-8 rounded-3xl border border-blue-200 dark:border-blue-500/20 mb-8">
                  <div className="flex items-start space-x-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl text-white flex-shrink-0">
                      <Lightbulb className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 text-lg">Quick Wins with AI</h3>
                      <p className="text-blue-800 dark:text-blue-200">
                        Most businesses can see initial AI benefits within 30-60 days by starting with customer service chatbots, email automation, or basic process automation. These "quick wins" help build momentum and prove ROI before investing in more complex solutions.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Implementation Steps */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">The 7-Step AI Implementation Framework</h2>
                
                <div className="space-y-8">
                  {implementationSteps.map((step, index) => (
                    <div key={index} className="bg-white/80 dark:bg-white/5 p-8 rounded-3xl border border-gray-200 dark:border-white/10">
                      <div className="flex items-start space-x-6">
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl flex items-center justify-center text-xl font-bold shadow-lg">
                            {step.step}
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">
                            {step.title}
                          </h3>
                          <p className="text-gray-700 dark:text-gray-300 mb-4">
                            {step.description}
                          </p>
                          <ul className="space-y-2">
                            {step.details.map((detail, dIndex) => (
                              <li key={dIndex} className="flex items-start space-x-2">
                                <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-600 dark:text-gray-400 text-sm">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* AI Solutions Comparison */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Popular AI Solutions: Implementation Time and ROI</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {aiSolutions.map((solution, index) => (
                    <div key={index} className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 p-6 rounded-xl border border-gray-200 dark:border-gray-600">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{solution.title}</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Implementation Time:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{solution.timeframe}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Expected ROI:</span>
                          <span className="font-medium text-blue-600 dark:text-blue-400">{solution.roi}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Difficulty:</span>
                          <span className={`font-medium ${
                            solution.difficulty === 'Low' ? 'text-blue-600 dark:text-blue-400' :
                            solution.difficulty === 'Medium' ? 'text-blue-700 dark:text-blue-300' :
                            'text-blue-800 dark:text-blue-200'
                          }`}>
                            {solution.difficulty}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Common Mistakes */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">5 Common AI Implementation Mistakes to Avoid</h2>
                
                <div className="space-y-6">
                  {commonMistakes.map((item, index) => (
                    <div key={index} className="bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/20 dark:to-transparent p-8 rounded-3xl border border-blue-200 dark:border-blue-500/20">
                      <div className="flex items-start space-x-4">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-xl text-white flex-shrink-0">
                          <AlertCircle className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 text-lg">
                            Mistake: {item.mistake}
                          </h3>
                          <p className="text-blue-800 dark:text-blue-200">
                            <strong>Solution:</strong> {item.solution}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ Section */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Frequently Asked Questions About AI Implementation</h2>
                
                <div className="space-y-6">
                  <div className="bg-white/80 dark:bg-white/5 p-6 rounded-xl border border-gray-200 dark:border-white/10">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">How much does AI implementation cost for small businesses?</h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      AI implementation costs vary widely based on complexity, scope, and specific business requirements. Small businesses may find solutions ranging from basic automation tools to comprehensive AI systems. Pricing depends on numerous factors including customization needs, integration requirements, and ongoing support. ROI timelines vary based on implementation success, user adoption, and business factors. Contact providers directly for accurate, customized pricing information.
                    </p>
                  </div>
                  
                  <div className="bg-white/80 dark:bg-white/5 p-6 rounded-xl border border-gray-200 dark:border-white/10">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">How long does it take to see results from AI implementation?</h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      Results timelines vary significantly based on solution complexity, implementation quality, user adoption, and business environment. Simpler AI solutions may show initial impact sooner, while more complex implementations may take several months to demonstrate value. Success depends on proper planning, execution, and organizational factors. Individual results will vary based on specific circumstances and implementation approach.
                    </p>
                  </div>
                  
                  <div className="bg-white/80 dark:bg-white/5 p-6 rounded-xl border border-gray-200 dark:border-white/10">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Do I need technical expertise to implement AI in my business?</h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      While technical expertise is helpful, it's not required. Many successful AI implementations are led by business leaders who partner with experienced AI providers. The key is focusing on business outcomes rather than technical details and working with providers who offer comprehensive training and support.
                    </p>
                  </div>
                </div>
              </section>

              {/* Conclusion */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Ready to Start Your AI Journey?</h2>
                
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  Implementing AI doesn't have to be overwhelming. By following this step-by-step framework and starting with small pilot projects, you can successfully integrate AI into your business operations and achieve measurable results.
                </p>
                
                <p className="text-gray-700 dark:text-gray-300 mb-8">
                  The businesses that implement AI today will have significant competitive advantages tomorrow. Don't wait—start your AI transformation journey now with proven solutions and expert guidance.
                </p>
              </section>

              {/* CTA Section */}
              <section className="mb-12">
                <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10 text-center">
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Get Expert AI Implementation Support</h3>
                  <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
                    Let our experienced team guide you through every step of your AI implementation journey
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                      to="/contact"
                      className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
                    >
                      Schedule Free Consultation
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                    <Link
                      to="/services"
                      className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300 inline-flex items-center justify-center"
                    >
                      View AI Services
                    </Link>
                  </div>
                </div>
              </section>

              {/* Legal Disclaimer */}
              <section className="mb-12">
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 text-sm text-gray-600 dark:text-gray-400">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Important Disclaimers</h3>
                  <p className="mb-2">
                    <strong>*Results Disclaimer:</strong> All timelines, cost estimates, and performance improvements mentioned in this guide are based on general industry observations and individual case studies. Results vary significantly based on business size, industry, implementation approach, user adoption, existing systems, and numerous other factors. Past performance does not guarantee future results.
                  </p>
                  <p className="mb-2">
                    <strong>Educational Purpose:</strong> This content is provided for educational and informational purposes only and does not constitute professional business, technical, or financial advice. Readers should consult with qualified professionals for advice specific to their situation.
                  </p>
                  <p className="mb-2">
                    <strong>No Guarantees:</strong> While AI implementation can provide significant benefits, success is not guaranteed. Implementation challenges, technical limitations, and market factors can affect outcomes.
                  </p>
                  <p>
                    <strong>Consultation Recommended:</strong> Before implementing AI solutions, businesses should conduct thorough assessments, consider their specific needs, and work with qualified AI implementation partners to develop appropriate strategies.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
    </>
  );
};